/* global debounce */

if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.appCache = new Map();
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = this.querySelector('[name="filter.p.product_type"]');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');
      this.addListeners();
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      const handleInput = debounce(this.handleInput.bind(this), (window.SearchAndFilterEmbed?.version === 1 && window.SearchAndFilterEmbed.search === true) ? 150 : 300);
      this.input.addEventListener('input', () => {
        // Invalidate keyboard selection synchronously, before the debounced request starts.
        this.close();
        this.removeAttribute('results');
        this.results.innerHTML = '';
        if (this.getQuery()) handleInput();
      });
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.querySelector('.search').addEventListener('submit', this.handleSubmit.bind(this));
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      if (
        !this.getQuery().length
        || this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')
      ) {
        evt.preventDefault();
      }
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.results.innerHTML = '';
        this.removeAttribute('results');
        this.close();
        return;
      }

      this.getResults(searchTerm);
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
      const query = this.getQuery();
      if (query.length > 0) {
        this.getResults(query);
      }
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
      const searchTerm = this.getQuery();
      if (!searchTerm.length) return;

      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getResults(searchTerm);
      }
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches('.tablist__tab')) return;

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (this.hasAttribute('open')) {
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          this.selectOption();
          break;

        case 'Escape':
          this.close();
          break;

        // no default
      }
    }

    /**
     * Handles 'keyup' events on the custom element.
     */
    handleKeyup() {
      // If search field is empty after key press, close the results.
      if (!this.getQuery().length) this.close();
    }

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      const allResults = this.getNavigableResults();
      const selectedResult = allResults.find((item) => item.getAttribute('aria-selected') === 'true');
      let resultToSelect = allResults[0];

      if (key === 'ArrowUp') {
        if (!selectedResult) return;

        // Select the next result up or the last one if we've reached the top of the list.
        resultToSelect = allResults[(allResults.indexOf(selectedResult) - 1 + allResults.length) % allResults.length];
      } else if (key === 'ArrowDown') {
        if (selectedResult) {
          // Select the next result down or the first one if we've reached the bottom of the list.
          resultToSelect = allResults[(allResults.indexOf(selectedResult) + 1) % allResults.length];
        }
      }

      this.statusEl.textContent = '';

      if (resultToSelect) {
        // If the selected item didn't change, do nothing.
        if (resultToSelect === selectedResult) return;

        this.results.querySelectorAll('.predictive-search__item[aria-selected="true"]').forEach((item) => {
          item.setAttribute('aria-selected', 'false');
        });
        resultToSelect.setAttribute('aria-selected', 'true');

        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute('aria-activedescendant', resultToSelect.id);
        // T26: Keep keyboard selection clear of the sticky tabs and View all row.
        resultToSelect.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    }

    getNavigableResults() {
      return Array.from(this.results.querySelectorAll('.predictive-search__item'))
        .filter((item) => !item.closest('[hidden]'));
    }

    /**
     * Selects a result
     */
    selectOption() {
      const selectedResult = this.getNavigableResults().find((item) => item.getAttribute('aria-selected') === 'true');
      selectedResult?.querySelector('a, button')?.click();
    }

    /**
     * Gets the results for a search term.
     * @param {string} searchTerm - Search query.
     */
    async getResults(searchTerm) {
      this.cancelRequest();
      if (!searchTerm || this.getQuery() !== searchTerm) return;
      const controller = new AbortController();
      this.requestController = controller;
      this.setLiveRegionLoadingState();

      try {
        let markup;
        // The app predictive API cannot filter by product type. Keep Shopify's filtered path.
        if ((window.SearchAndFilterEmbed?.version === 1 && window.SearchAndFilterEmbed.search === true) && !this.productTypeInput?.value) {
          try {
            markup = await this.getAppResults(searchTerm, controller.signal);
          } catch (error) {
            if (controller.signal.aborted) return;
          }
        }
        if (markup === undefined) markup = await this.getNativeResults(searchTerm, controller.signal);
        if (!controller.signal.aborted && this.getQuery() === searchTerm) this.renderResults(markup);
      } catch (error) {
        if (!controller.signal.aborted) this.close();
      }
    }

    cancelRequest() {
      this.requestController?.abort();
    }

    /** Fetch and consume the body under one deadline, including stalled response bodies. */
    async fetchResults(url, signal, format, credentials = 'same-origin') {
      const controller = new AbortController();
      const abort = () => controller.abort();
      signal.addEventListener('abort', abort, { once: true });
      if (signal.aborted) abort();
      const timer = setTimeout(abort, 6000);
      try {
        const response = await fetch(url, { signal: controller.signal, credentials });
        if (!response.ok) throw new Error('Search request failed');
        const body = await response[format]();
        if (controller.signal.aborted) throw new Error('Search request cancelled');
        return body;
      } finally {
        clearTimeout(timer);
        signal.removeEventListener('abort', abort);
      }
    }

    storefrontURL(path) {
      const url = new URL(path, window.location.origin);
      const current = new URL(window.location.href);
      ['preview_theme_id', '_fd', 'pb'].forEach((key) => {
        if (current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
      });
      return url;
    }

    canRenderAppProducts(context) {
      return this.dataset.presentationCustomer === 'false' && this.dataset.appPrimaryLocale === 'true'
        && context?.supported === true && context.version === 1 && context.pricing_scope === 'uniform-catalog'
        && context.currency === this.dataset.presentationCurrency
        && /^[A-Z]{3}$/.test(context.currency)
        && Number.isFinite(context.verified_at) && Number.isFinite(context.expires_at)
        && context.verified_at <= Date.now() && context.verified_at > Date.now() - 60000
        && context.expires_at > Date.now() && context.expires_at <= context.verified_at + 60000;
    }

    // Liquid supplies the exact money markup (including supercents/currency).
    // Replace numeric text only; unsupported formats retain Shopify rendering.
    fillAppMoney(element, cents) {
      let integers = 0;
      let decimals = 0;
      const visit = (node) => {
        if (node.nodeType === 3) {
          node.textContent = node.textContent.replace(/1([,. '\u00a0\u202f])234\1[5]67|1234567|89/g, (match, separator) => {
            if (match === '89') { decimals++; return String(cents % 100).padStart(2, '0'); }
            integers++;
            const integer = String(Math.floor(cents / 100));
            return separator ? integer.replace(/\B(?=(\d{3})+(?!\d))/g, separator) : integer;
          });
        } else node.childNodes.forEach(visit);
      };
      visit(element);
      if (integers !== 1 || decimals !== 1) throw new Error('Unsupported native money format');
    }

    renderAppProduct(product, entry, index) {
      const display = product.enterprise_predictive;
      if (!display || display.version !== 1 || display.supported !== true || display.handle !== entry.handle
        || !Number.isSafeInteger(display.price_cents) || display.price_cents < 0
        || (display.compare_at_price_cents !== null && (!Number.isSafeInteger(display.compare_at_price_cents) || display.compare_at_price_cents < 0))
        || ['title', 'code', 'vendor', 'image_alt'].some(key => typeof display[key] !== 'string')
        || ['available', 'on_sale', 'price_varies'].some(key => typeof display[key] !== 'boolean')
        || !['', '/yd', '/piece'].includes(display.unit)
        || display.on_sale !== (display.compare_at_price_cents > display.price_cents)
        || !new RegExp(`^/products/${entry.handle}(?:\\?variant=\\d+)?$`).test(display.url || '')) {
        throw new Error('Unsupported predictive product');
      }
      const template = this.querySelector('[data-app-product-template]');
      if (!template || template.dataset.moneySupported === 'false') throw new Error('Product template unavailable');
      const item = template.content.firstElementChild.cloneNode(true);
      item.dataset.productHandle = entry.handle;
      item.id = `${this.input.id}-products-result-${index + 1}`;
      item.querySelector('.js-search-link').setAttribute('href', `${entry.url.pathname}${entry.url.search}`);
      ['title', 'code', 'vendor'].forEach(key => {
        const element = item.querySelector(`[data-app-${key}]`);
        if (element) { element.textContent = display[key]; if (!display[key]) element.remove(); }
      });
      const image = item.querySelector('[data-app-image]');
      if (display.image_url) {
        const url = new URL(display.image_url);
        if (url.protocol !== 'https:' || url.username || url.password) throw new Error('Invalid product image');
        const sized = (width) => { const sizedURL = new URL(url); sizedURL.searchParams.set('width', width); return sizedURL.href; };
        image.src = sized(176);
        image.srcset = [88, 144, 176].map(width => `${sized(width)} ${width}w`).join(', ');
        image.sizes = '(min-width: 769px) 88px, 72px';
        image.loading = 'eager';
        image.alt = display.image_alt;
        if (Number.isSafeInteger(display.image_width) && display.image_width > 0) image.width = display.image_width;
        if (Number.isSafeInteger(display.image_height) && display.image_height > 0) image.height = display.image_height;
        item.querySelector('[data-app-placeholder]').remove();
      } else {
        image.remove();
        item.querySelector('.predictive-result__media').classList.remove('image-blend');
      }
      const price = item.querySelector('.price');
      if (price) {
        price.classList.toggle('price--on-sale', display.on_sale);
        price.classList.toggle('price--sold-out', !display.available);
        price.querySelector(display.on_sale ? '[data-app-regular-label]' : '[data-app-sale-label]').remove();
        if (!display.price_varies) price.querySelector('.price__from').remove();
        this.fillAppMoney(price.querySelector('.price__current .js-value'), display.price_cents);
        const was = price.querySelector('.price__was');
        if (display.on_sale) this.fillAppMoney(was.querySelector('.js-value'), display.compare_at_price_cents);
        else { was.querySelector('.js-label').textContent = ''; was.querySelector('.js-value').textContent = ''; }
        const unit = price.querySelector('.t26-predictive-result__unit');
        if (display.unit) unit.textContent = display.unit; else unit.remove();
      }
      return item;
    }

    async getAppResults(searchTerm, signal) {
      const cacheKey = JSON.stringify([searchTerm, this.dataset.presentationCurrency, this.dataset.presentationCountry,
        this.dataset.presentationMarket, this.dataset.presentationCustomer, this.dataset.appPrimaryLocale]);
      const cached = this.appCache.get(cacheKey);
      if (cached && Date.now() < cached.expires) return cached.markup;
      const controller = new AbortController();
      const abort = () => controller.abort();
      signal.addEventListener('abort', abort, { once: true });
      if (signal.aborted) abort();
      const timer = setTimeout(abort, 8000);
      try {
        const api = new URL('https://search-filter.thetex.co.kr/api/storefront/predictive-search');
        api.searchParams.set('q', searchTerm);
        api.searchParams.set('limit', Math.min(Number(theme.settings.pSearchLimit) || 5, 5));
        api.searchParams.set('presentation', 'enterprise-v1');
        const data = await this.fetchResults(api.href, controller.signal, 'json', 'omit');
        if (data.ok !== true || data.enabled !== true || !Array.isArray(data.products)) {
          throw new Error('App search unavailable');
        }

        const template = this.querySelector('[data-app-results-template]');
        if (!template) throw new Error('App search template missing');
        // Keep custom elements inert until panels and their active state are ready for insertion.
        const markup = document.createElement('template');
        markup.innerHTML = template.innerHTML.replace(/psearch/g, this.input.id);
        const panel = markup.content.querySelector('[role="tabpanel"]');
        const tab = markup.content.querySelector('[role="tab"]');
        panel.hidden = false;
        tab.setAttribute('aria-selected', 'true');
        tab.removeAttribute('tabindex');
        const footer = panel.querySelector('.js-submit');
        const suggestions = Array.isArray(data.suggestions)
          ? data.suggestions.filter((suggestion) => typeof suggestion?.text === 'string' && suggestion.text.trim()).slice(0, 5)
          : [];
        const suggestionsColumn = markup.content.querySelector('.predictive-search__suggestions');
        if (suggestions.length) {
          const list = suggestionsColumn.querySelector('ul');
          const root = (this.dataset.rootUrl || '/').replace(/\/$/, '');
          suggestions.forEach((suggestion, index) => {
            const item = document.createElement('li');
            item.className = 'predictive-search__item';
            item.id = `${this.input.id}-suggestions-result-${index + 1}`;
            item.setAttribute('role', 'option');
            const link = document.createElement('a');
            link.className = 'predictive-result predictive-result--suggestion block focus-inset js-search-link';
            // The API URL is untrusted; rebuild a locale-aware storefront search from plain text.
            const url = this.storefrontURL(`${root}/search`);
            url.searchParams.set('q', suggestion.text);
            url.searchParams.set('type', 'product');
            link.setAttribute('href', `${url.pathname}${url.search}`);
            link.textContent = suggestion.text;
            item.append(link);
            list.append(item);
          });
          markup.content.querySelector('.predictive-search__scroller').classList.add(
            data.products.length ? 'predictive-search__scroller--suggestions' : 'predictive-search__scroller--suggestions-only'
          );
        } else {
          suggestionsColumn?.remove();
        }
        if (data.products.length > 5) throw new Error('Invalid predictive product count');
        const entries = data.products.map((product) => {
          if (!product || !/^[a-z0-9][a-z0-9-]*$/i.test(product.handle || '')) throw new Error('Invalid product handle');
          const root = (this.dataset.rootUrl || '/').replace(/\/$/, '');
          const url = this.storefrontURL(`${root}/products/${product.handle}`);
          let variant = '';
          if (product.variant_url) {
            const variantURL = new URL(product.variant_url, window.location.origin);
            if (!['http:', 'https:'].includes(variantURL.protocol)
              || !variantURL.pathname.endsWith(`/products/${product.handle}`)) throw new Error('Invalid variant URL');
            variant = variantURL.searchParams.get('variant') || '';
            if (variant && !/^\d+$/.test(variant)) throw new Error('Invalid variant ID');
            if (variant) url.searchParams.set('variant', variant);
          }
          return { handle: product.handle, key: `${product.handle}~${variant}`, url };
        });
        let cards = [];
        let direct = false;
        if (entries.length && this.canRenderAppProducts(data.presentation_context)) {
          try {
            cards = data.products.map((product, index) => this.renderAppProduct(product, entries[index], index));
            direct = true;
          } catch (error) { cards = []; }
        }
        if (entries.length && !cards.length) {
          const root = (this.dataset.rootUrl || '/').replace(/\/$/, '');
          const url = this.storefrontURL(`${root}/search`);
          url.searchParams.set('section_id', 't26-predictive-product');
          url.searchParams.set('q', `t26cards:${entries.map(({ key }) => key).join(',')}`);
          const html = await this.fetchResults(url.href, controller.signal, 'text');
          const fragment = document.createElement('template');
          fragment.innerHTML = html;
          entries.forEach((entry, index) => {
            const item = fragment.content.querySelector(`.predictive-search__item[data-product-handle="${entry.handle}"]`);
            const link = item?.querySelector('.js-search-link');
            if (!item || !link) throw new Error('Predictive product fragment missing');
            item.id = `${this.input.id}-products-result-${index + 1}`;
            link.setAttribute('href', `${entry.url.pathname}${entry.url.search}`);
            cards.push(item);
          });
        }
        if (controller.signal.aborted) throw new Error('App search cancelled');
        cards.forEach((item) => footer.before(item));
        if (!cards.length) {
          markup.content.querySelector('tabbed-content').remove();
          if (!suggestions.length) {
            const empty = markup.content.querySelector('.predictive-search__no-results');
            empty.hidden = false;
            empty.textContent = empty.textContent.replace('__T26_QUERY__', searchTerm);
          }
        }
        if (this.appCache.size >= 20) this.appCache.delete(this.appCache.keys().next().value);
        this.appCache.set(cacheKey, { markup: markup.innerHTML, expires: direct ? data.presentation_context.expires_at : Date.now() + 60000 });
        return markup.innerHTML;
      } finally {
        controller.abort();
        clearTimeout(timer);
        signal.removeEventListener('abort', abort);
      }
    }

    async getNativeResults(searchTerm, signal) {

      let searchFields = 'title,product_type,variants.title,vendor';
      if (theme.settings.pSearchIncludeSkus) searchFields += ',variants.sku';
      if (theme.settings.pSearchIncludeTags) searchFields += ',tag';

      let searchParams = '';
      if (this.productTypeInput && this.productTypeInput.value !== '') {
        searchParams = `q=product_type:${encodeURIComponent(this.productTypeInput.value)} AND ${encodeURIComponent(searchTerm)}`;
      } else {
        searchParams = `q=${encodeURIComponent(searchTerm)}`;
      }

      searchParams += `&${encodeURIComponent('resources[limit]')}=${theme.settings.pSearchLimit}`;
      searchParams += `&${encodeURIComponent('resources[limit_scope]')}=${
        theme.settings.pSearchLimitScope
      }`;
      searchParams += `&${encodeURIComponent('resources[options][fields]')}=${searchFields}`;
      searchParams += '&section_id=predictive-search';

      const queryKey = searchParams.replace(' ', '-').toLowerCase();
      if (this.cachedResults[queryKey]) {
        return this.cachedResults[queryKey];
      }

      const html = await this.fetchResults(this.storefrontURL(`${theme.routes.predictiveSearch}?${searchParams}`).href, signal, 'text');

      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      const resultsEl = tmpl.content.querySelector('#shopify-section-predictive-search');
      const resultsMarkup = resultsEl.innerHTML.replace(/psearch/g, this.input.id);

      this.cachedResults[queryKey] = resultsMarkup;
      return resultsMarkup;
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup) {
      this.results.innerHTML = resultsMarkup;
      this.results.querySelectorAll('.predictive-search__item').forEach((item, index) => {
        if (!item.id) item.id = `${this.input.id}-result-${index + 1}`;
      });
      this.setAttribute('results', true);
      this.open();
    }

    /**
     * Opens the results listbox.
     */
    open() {
      this.overlay.classList.add('is-visible');
      if (this.getQuery().length) this.resetBtn.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      document.body.classList.add('overlay-predictive-search');

      // Add event handlers (so the bound event listeners can be removed).
      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.keyupHandler = this.keyupHandler || this.handleKeyup.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);

      // Add event listeners (for while results are open).
      this.addEventListener('keydown', this.keydownHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      this.overlay.addEventListener('click', this.overlayClickHandler);
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close(evt) {
      this.cancelRequest();
      // If reset button was clicked, empty the input field.
      if (evt && evt.target === this.resetBtn) {
        this.input.value = '';
        this.results.innerHTML = '';
        this.removeAttribute('results');
        this.input.focus();
      }

      // Deselect the selected result (if there is one).
      const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');

      this.removeAttribute('open');
      this.resetBtn.hidden = true;
      this.overlay.classList.remove('is-visible');
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overlay-predictive-search');

      // Remove event listeners added on search results opening.
      this.removeEventListener('keydown', this.keydownHandler);
      this.removeEventListener('keyup', this.keyupHandler);
      this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      this.overlay.removeEventListener('click', this.overlayClickHandler);
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
